import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from '@org/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// ── Carousel wrapper ──────────────────────────────────────────────

interface CarouselWrapperProps {
  children: React.ReactNode;
  /** Index of the card to center in the viewport on mount */
  centerIndex?: number;
}

export function CarouselWrapper({ children, centerIndex }: CarouselWrapperProps) {
  const { t } = useTranslation('admin');
  const viewportRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);
  const [dragging, setDragging] = useState(false);

  // Recompute which edges are reached → drives the fade masks + arrow enablement.
  const updateEdges = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    setAtStart(vp.scrollLeft <= 1);
    setAtEnd(vp.scrollLeft + vp.clientWidth >= vp.scrollWidth - 1);
  }, []);

  // Center on the active card on mount, then sync edge state.
  useEffect(() => {
    const viewport = viewportRef.current;
    const inner = innerRef.current;
    if (!viewport || !inner) return;
    if (centerIndex != null) {
      const card = inner.children[centerIndex] as HTMLElement | undefined;
      if (card) {
        const left = card.offsetLeft + card.offsetWidth / 2 - viewport.clientWidth / 2;
        viewport.scrollTo({ left, behavior: 'instant' as ScrollBehavior });
      }
    }
    requestAnimationFrame(updateEdges);
  }, [centerIndex, updateEdges]);

  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [updateEdges]);

  // Arrow paging — advance by roughly one card width (card + gap).
  const page = useCallback((dir: number) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const firstCard = innerRef.current?.children[0] as HTMLElement | undefined;
    const step = firstCard ? firstCard.offsetWidth + 24 : Math.round(vp.clientWidth * 0.8);
    vp.scrollBy({ left: step * dir, behavior: 'smooth' });
  }, []);

  // Desktop mouse drag-to-scroll (Pointer Events). Touch is left to native
  // scrolling; a real drag suppresses the click so a card never opens by accident.
  const drag = useRef({ down: false, moved: false, startX: 0, startScroll: 0 });
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    const vp = viewportRef.current;
    if (!vp) return;
    drag.current = { down: true, moved: false, startX: e.clientX, startScroll: vp.scrollLeft };
    // No pointer capture here: capturing retargets the eventual click to the
    // viewport, so plain clicks on cards would never reach their <Link>.
    // Capture starts in onPointerMove once a real drag is detected.
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const st = drag.current;
    if (!st.down) return;
    // Button released outside the viewport (no capture yet) → stale drag state.
    if (e.buttons === 0) {
      st.down = false;
      return;
    }
    const dx = e.clientX - st.startX;
    if (!st.moved && Math.abs(dx) > 5) {
      st.moved = true;
      setDragging(true);
      viewportRef.current?.setPointerCapture(e.pointerId);
    }
    if (st.moved && viewportRef.current) viewportRef.current.scrollLeft = st.startScroll - dx;
  };
  const endDrag = (e: React.PointerEvent) => {
    const st = drag.current;
    if (!st.down) return;
    st.down = false;
    const vp = viewportRef.current;
    try {
      vp?.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already released */
    }
    setDragging(false);
    if (st.moved && vp) {
      const suppress = (ev: Event) => {
        ev.stopPropagation();
        ev.preventDefault();
        vp.removeEventListener('click', suppress, true);
      };
      vp.addEventListener('click', suppress, true);
    }
  };

  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10 rounded-[36px] bg-gradient-to-br from-[#fdecef] via-white to-[#fff8fb] opacity-80 blur-3xl" />

      {/* Edge fade masks — "there's more" cue; purely visual, never block pointers. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 left-0 z-[2] w-8 bg-gradient-to-r from-white to-transparent transition-opacity duration-200 sm:w-14 ${atStart ? 'opacity-0' : 'opacity-100'}`}
      />
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 right-0 z-[2] w-8 bg-gradient-to-l from-white to-transparent transition-opacity duration-200 sm:w-14 ${atEnd ? 'opacity-0' : 'opacity-100'}`}
      />

      {/* Arrows — mouse & keyboard affordance; hidden on touch-first small screens. */}
      <button
        type="button"
        onClick={() => page(-1)}
        disabled={atStart}
        aria-label={t('p2e.classes.carousel.prev', { defaultValue: 'Previous classes' })}
        className="absolute left-0 top-1/2 z-[3] hidden h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-rose-200 bg-white text-[#cf2e2e] shadow-sm transition hover:bg-[#cf2e2e] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 disabled:pointer-events-none disabled:opacity-0 sm:grid"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={() => page(1)}
        disabled={atEnd}
        aria-label={t('p2e.classes.carousel.next', { defaultValue: 'More classes' })}
        className="absolute right-0 top-1/2 z-[3] hidden h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-rose-200 bg-white text-[#cf2e2e] shadow-sm transition hover:bg-[#cf2e2e] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 disabled:pointer-events-none disabled:opacity-0 sm:grid"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <div
        ref={viewportRef}
        tabIndex={0}
        role="region"
        aria-label={t('p2e.classes.carousel.region', { defaultValue: 'Classes — scroll horizontally' })}
        onScroll={updateEdges}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className={`w-full snap-x snap-proximity overflow-x-auto pb-4 [scrollbar-width:none] focus-visible:outline-none [&::-webkit-scrollbar]:hidden ${
          dragging ? 'cursor-grabbing select-none [scroll-snap-type:none]' : 'cursor-grab'
        }`}
      >
        <div ref={innerRef} className="flex min-w-max gap-6 [&>*]:snap-start">
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Empty state card ──────────────────────────────────────────────

interface EmptyCarouselCardProps {
  eyebrow: string;
  title: string;
  description: string;
}

export function EmptyCarouselCard({ eyebrow, title, description }: EmptyCarouselCardProps) {
  return (
    <Card className="rounded-[28px] border-dashed border-rose-200 bg-[#fff6f9] p-12 text-center shadow-sm">
      <CardHeader>
        <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">{eyebrow}</p>
        <CardTitle className="text-2xl text-gray-900">{title}</CardTitle>
        <CardDescription className="text-gray-600">{description}</CardDescription>
      </CardHeader>
    </Card>
  );
}
